import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { GridModule } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { MenuModule } from '@progress/kendo-angular-menu';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FloatingLabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LabelModule } from '@progress/kendo-angular-label';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { IconsModule } from '@progress/kendo-angular-icons';
import { AppRoutingModule } from './app-routing.module';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DatePipe } from '@angular/common';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogModule, DialogRef, DialogsModule } from '@progress/kendo-angular-dialog';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClientJsonpModule } from '@angular/common/http';
import { IPublicClientApplication, PublicClientApplication, InteractionType } from '@azure/msal-browser';
import {
  MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService,
  MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent
} from '@azure/msal-angular';
import { msalConfig, loginRequest, protectedResources } from './auth-config';
import { AwardInformationEntryComponent } from './award/award-information-entry/award-information-entry.component';
import { DocumentComponent } from './common/components/document/document.component';
import { DeliverableEntryComponent } from './award/deliverable-entry/deliverable-entry.component';
import { DonorInformationEntryComponent } from './Donor/donor-information-entry/donor-information-entry.component';
import { DonorListComponent } from './Donor/donor-list/donor-list.component';
import { ErrorPageComponent } from './error-page/error-page.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { PopupModule } from '@progress/kendo-angular-popup';
import { UploadModule } from '@progress/kendo-angular-upload';
import { CanDeactivateGuard } from './can-deactivate/can-deactivate.guard';
// import { AuthInterceptor } from './auth.interceptor';
import { PriorApprovalsComponent } from './award/prior-approvals/prior-approvals.component';
import { NotesComponent } from './award/notes/notes.component';
import { AuditComponent } from './common/components/audit/audit.component';
import { MatSelectModule } from '@angular/material/select';
import { ErrorContentComponent } from './common/components/error-message/auth.interceptor';
import {  AuthInterceptor } from './common/components/error-message/auth.interceptor';
import { DataMigrationComponent } from './award/data-migratrion/data-migration/data-migration.component';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { ReportComponent } from './report/report/report.component';
import { AuthorizedErrorComponent } from './common/components/authorized-error/authorized-error/authorized-error.component';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap.set(protectedResources.agreementStatusApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getCountryApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDonorAgencyApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getActivityApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getTechnicalAreaApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getContactApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getRegionApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postAwardInformationApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postAgreementDetailsApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getAgreementsApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDonorContactsApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postSaveAgreementCountryApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postSaveAgreementRegionApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postSaveDonorContactApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postAgreementTechnicalAreaApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postCountryImplementationApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteCountryImplementationApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getAgreementInformationDetailsApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postAgreementActivityApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteAgreementCountryApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteAgreementRegionApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteDonorContactApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDonorsListApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDonorInformationApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postDonorInformationApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postDonorContactApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getNotificationFrequency.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDeliverablesType.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDocumentsType.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDocumentDetails.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.saveDocuments.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteDocuments.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getAwardContacts.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteAgreementDonorContactApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteDonorInfoEntryContacts.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.uploadDocument.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.downloadDocument.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getAllCountry.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getRegionCountry.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteDocumentFile.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDeliverableInformation.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postDeliverableInformation.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteDeliverableInformation.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDeliverablePeriod.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDeliverablePeriodValue.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getFunder.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.saveNotes.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getNotes.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteNotes.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getPriorApprovalsType.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getPriorApprovalsInformation.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.savePriorApprovalsInformation.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deletePriorApprovals.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getAwardAudit.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getAuditDocuments.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getAllUsers.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteTechnicalAreaApi.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postDataMigration.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getDeliverableDetails.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.postDeliverableDetails.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.deleteDeliverableDetails.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getAdminCheck.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.getIsAwardYearUsed.endpoint, protectedResources.agreementStatusApi.scopes);
  protectedResourceMap.set(protectedResources.documentDataMigration.endpoint, protectedResources.agreementStatusApi.scopes);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest
  };
}

@NgModule({
  declarations: [
    AppComponent,
    ErrorContentComponent,
    HeaderComponent,
    DashboardComponent,
    AwardInformationEntryComponent,
    DocumentComponent,
    DeliverableEntryComponent,
    DonorInformationEntryComponent,
    DonorListComponent,
    ErrorPageComponent,
    PageNotFoundComponent,
    PriorApprovalsComponent,
    NotesComponent,
    AuditComponent,
    DataMigrationComponent,
    ReportComponent,
    AuthorizedErrorComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    GridModule,
    MenuModule,
    InputsModule,
    FloatingLabelModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    HttpClientModule,
    HttpClientJsonpModule,
    DropDownsModule,
    LabelModule,
    ButtonsModule,
    LayoutModule,
    IconsModule,
    DateInputsModule,
    DialogModule,
    DialogsModule,
    MatButtonModule,
    MatToolbarModule,
    MatListModule,
    MatTableModule,
    MatCardModule,
    MatInputModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatIconModule,
    FormsModule,
    MsalModule,
    PopupModule,
    UploadModule,
    NotificationModule,
    MatSelectModule,
    PdfViewerModule,
    NgxDocViewerModule,
    TooltipModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    { provide: DialogRef, useValue: {} },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    [CanDeactivateGuard],
  ],
  entryComponents: [ErrorContentComponent],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
